import React, { useState, useEffect, useMemo } from 'react';
import { db } from '../firebase/config';
import { 
  collection, getDocs, doc, setDoc, getDoc, updateDoc 
} from 'firebase/firestore';

// Helper: format number with thousand separators (UGX)
const formatMoney = (value) => {
  if (value === '' || value === undefined || value === null) return '';
  const num = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
  if (isNaN(num)) return '';
  return num.toLocaleString('en-US');
};

// Helper: parse formatted string to number
const parseMoney = (formatted) => {
  if (!formatted) return '';
  const cleaned = formatted.replace(/,/g, '');
  const num = parseFloat(cleaned);
  return isNaN(num) ? '' : num;
};

export const ReceivablesVsPayables = () => {
  const [receivables, setReceivables] = useState([]);
  const [payables, setPayables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [planningData, setPlanningData] = useState({});
  const [saving, setSaving] = useState({});
  
  const [groupPages, setGroupPages] = useState({});
  const itemsPerGroupPage = 15;
  
  const [completedPage, setCompletedPage] = useState(1);
  const [ongoingPage, setOngoingPage] = useState(1);
  const ordersPerPage = 15;
  
  const [receivablesSearch, setReceivablesSearch] = useState('');

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [clientsSnapshot, ordersSnapshot, paymentsSnapshot, planningSnapshot, payableSnapshot, itemsSnapshot] = await Promise.all([
        getDocs(collection(db, 'clients')),
        getDocs(collection(db, 'orders')),
        getDocs(collection(db, 'payments')),
        getDocs(collection(db, 'cashFlowPlanning')),
        getDocs(collection(db, 'accountsPayable')),
        getDocs(collection(db, 'orderItems'))
      ]);

      const orderItemsMap = {};
      itemsSnapshot.docs.forEach(doc => {
        const item = { id: doc.id, ...doc.data() };
        if (!orderItemsMap[item.orderId]) orderItemsMap[item.orderId] = [];
        orderItemsMap[item.orderId].push(item);
      });

      const ordersByClient = {};
      ordersSnapshot.docs.forEach(doc => {
        const order = { id: doc.id, ...doc.data() };
        const clientId = order.clientId;
        if (!ordersByClient[clientId]) ordersByClient[clientId] = [];
        const items = orderItemsMap[order.id] || [];
        ordersByClient[clientId].push({
          ...order,
          items: items.map(it => ({
            name: it.productName,
            quantity: it.quantity,
            price: it.unitPrice,
            total: (it.quantity || 0) * (it.unitPrice || 0)
          }))
        });
      });

      const clientsMap = {};
      clientsSnapshot.docs.forEach(doc => {
        clientsMap[doc.id] = { id: doc.id, ...doc.data() };
      });

      const paymentsByClient = {};
      paymentsSnapshot.docs.forEach(doc => {
        const payment = doc.data();
        const clientId = payment.clientId;
        if (!paymentsByClient[clientId]) paymentsByClient[clientId] = 0;
        paymentsByClient[clientId] += payment.amount || 0;
      });

      const receivablesList = [];
      for (const [clientId, client] of Object.entries(clientsMap)) {
        const clientOrders = ordersByClient[clientId] || [];
        if (clientOrders.length === 0) continue;
        const ordersWithBalance = clientOrders.map(order => ({
          ...order,
          totalAmount: order.totalSellingPrice || order.items.reduce((s,i)=>s+i.total,0),
          balanceDue: order.balanceDue !== undefined ? order.balanceDue : 
                      (order.totalSellingPrice || order.items.reduce((s,i)=>s+i.total,0)) - (paymentsByClient[clientId] || 0)
        }));
        receivablesList.push({
          id: clientId,
          name: client.name,
          phone: client.phone,
          email: client.email,
          orders: ordersWithBalance
        });
      }
      setReceivables(receivablesList);

      const payablesData = payableSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        currentBalance: doc.data().currentBalance || 0
      }));
      setPayables(payablesData);

      const planningMap = {};
      planningSnapshot.docs.forEach(doc => {
        planningMap[doc.id] = {
          suggestion: doc.data().suggestion || 0,
          toBePaid: doc.data().toBePaid || 0
        };
      });
      setPlanningData(planningMap);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const savePlanningToFirestore = async (payableId, field, value) => {
    const numValue = value === '' ? 0 : parseFloat(value) || 0;
    const planningRef = doc(db, 'cashFlowPlanning', payableId);
    try {
      const docSnap = await getDoc(planningRef);
      if (docSnap.exists()) {
        await updateDoc(planningRef, { [field]: numValue });
      } else {
        await setDoc(planningRef, { [field]: numValue });
      }
    } catch (error) {
      console.error('Error saving planning data:', error);
    }
  };

  const updatePlanning = (payableId, field, rawValue) => {
    const numValue = rawValue === '' ? 0 : parseFloat(rawValue) || 0;
    setPlanningData(prev => ({
      ...prev,
      [payableId]: {
        ...prev[payableId],
        [field]: numValue
      }
    }));
    setSaving(prev => ({ ...prev, [`${payableId}_${field}`]: true }));
    setTimeout(async () => {
      await savePlanningToFirestore(payableId, field, numValue);
      setSaving(prev => ({ ...prev, [`${payableId}_${field}`]: false }));
    }, 500);
  };

  const getPlanning = (payableId, field) => planningData[payableId]?.[field] ?? 0;

  const groupedPayables = useMemo(() => {
    const groupDefinitions = {
      employee: { label: '👨‍💼 Employees', types: ['employee'] },
      carpenter: { label: '🪚 Carpenters', types: ['carpenter'] },
      subcontractor: { label: '👷 Subcontractors', types: ['subcontractor'] },
      supplier: { label: '🏭 Suppliers', types: ['supplier'] },
      loan: { label: '🏦 Loans', types: ['loan', 'loans'] },
      rent: { label: '🏠 Rent', types: ['rent'] },
      marketing: { label: '📢 Marketing', types: ['marketing'] },
      other: { label: '📋 Other', types: [] }
    };
    const groups = {};
    for (const [key, def] of Object.entries(groupDefinitions)) {
      groups[key] = { label: def.label, items: [], totalBalance: 0, totalPlanned: 0, totalSuggested: 0 };
    }
    payables.forEach(p => {
      const type = p.type || '';
      let groupKey = 'other';
      for (const [key, def] of Object.entries(groupDefinitions)) {
        if (def.types.includes(type)) {
          groupKey = key;
          break;
        }
      }
      groups[groupKey].items.push(p);
      groups[groupKey].totalBalance += p.currentBalance;
      groups[groupKey].totalPlanned += planningData[p.id]?.toBePaid || 0;
      groups[groupKey].totalSuggested += planningData[p.id]?.suggestion || 0;
    });
    const nonEmptyGroups = {};
    for (const [key, group] of Object.entries(groups)) {
      if (group.items.length > 0) nonEmptyGroups[key] = group;
    }
    return nonEmptyGroups;
  }, [payables, planningData]);

  const getPaginatedGroupItems = (groupId, items) => {
    const page = groupPages[groupId] || 1;
    const start = (page - 1) * itemsPerGroupPage;
    return {
      paginated: items.slice(start, start + itemsPerGroupPage),
      page,
      totalPages: Math.ceil(items.length / itemsPerGroupPage)
    };
  };
  const setGroupPage = (groupId, page) => setGroupPages(prev => ({ ...prev, [groupId]: page }));

  const filteredReceivables = useMemo(() => {
    if (!receivablesSearch) return receivables;
    const term = receivablesSearch.toLowerCase();
    return receivables.filter(r => r.name.toLowerCase().includes(term) || (r.phone && r.phone.includes(term)));
  }, [receivables, receivablesSearch]);

  const allCompletedOrders = useMemo(() => {
    const orders = [];
    filteredReceivables.forEach(client => {
      client.orders.forEach(order => {
        if (order.workStatus === 'completed' && (order.balanceDue || 0) > 0) {
          orders.push({ ...order, clientName: client.name, clientPhone: client.phone, clientId: client.id });
        }
      });
    });
    return orders;
  }, [filteredReceivables]);

  const allOngoingOrders = useMemo(() => {
    const orders = [];
    filteredReceivables.forEach(client => {
      client.orders.forEach(order => {
        const status = order.workStatus || 'pending';
        if ((status === 'ongoing' || status === 'pending') && (order.balanceDue || 0) > 0) {
          orders.push({ ...order, clientName: client.name, clientPhone: client.phone, clientId: client.id });
        }
      });
    });
    return orders;
  }, [filteredReceivables]);

  const paginatedCompletedOrders = useMemo(() => {
    const start = (completedPage - 1) * ordersPerPage;
    return allCompletedOrders.slice(start, start + ordersPerPage);
  }, [allCompletedOrders, completedPage]);

  const paginatedOngoingOrders = useMemo(() => {
    const start = (ongoingPage - 1) * ordersPerPage;
    return allOngoingOrders.slice(start, start + ordersPerPage);
  }, [allOngoingOrders, ongoingPage]);

  const totalCompletedPages = Math.ceil(allCompletedOrders.length / ordersPerPage);
  const totalOngoingPages = Math.ceil(allOngoingOrders.length / ordersPerPage);
  const totalCompletedBalance = allCompletedOrders.reduce((s, o) => s + o.balanceDue, 0);
  const totalOngoingBalance = allOngoingOrders.reduce((s, o) => s + o.balanceDue, 0);
  const totalPayablesBalance = payables.reduce((s, p) => s + (p.currentBalance || 0), 0);
  const totalPlannedPayments = Object.values(planningData).reduce((s, p) => s + (p.toBePaid || 0), 0);
  const totalSuggestions = Object.values(planningData).reduce((s, p) => s + (p.suggestion || 0), 0);

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.spinner}></div>
        <p>Loading cash flow planner...</p>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.twoColumns}>
        {/* LEFT: Accounts Receivable */}
        <div style={styles.column}>
          <div style={styles.columnHeader}>
            <h2 style={styles.columnTitle}>📋 Accounts Receivable</h2>
            <div style={styles.summaryCard}>
              <div><span style={styles.summaryLabel}>Completed Projects (Unpaid)</span><span style={styles.summaryValue}>{totalCompletedBalance.toLocaleString()} UGX</span></div>
              <div><span style={styles.summaryLabel}>Ongoing Projects</span><span style={styles.summaryValue}>{totalOngoingBalance.toLocaleString()} UGX</span></div>
            </div>
          </div>
          <div style={styles.searchBar}>
            <input type="text" placeholder="🔍 Search by client name or phone..." value={receivablesSearch} onChange={(e) => setReceivablesSearch(e.target.value)} style={styles.searchInput} />
          </div>

          {/* Completed Projects */}
          <div style={styles.groupSection}>
            <div style={styles.groupHeader}>
              <span style={styles.groupTitle}>✅ Completed Projects (Not Fully Paid)</span>
              <span style={styles.groupTotals}>Outstanding: {totalCompletedBalance.toLocaleString()} UGX</span>
            </div>
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead><tr style={styles.tableHeaderRow}>
                  <th>Client</th><th>Order #</th><th>Products</th><th>Total (UGX)</th><th>Balance Due</th>
                </tr></thead>
                <tbody>
                  {paginatedCompletedOrders.map((order, idx) => (
                    <tr key={idx} style={styles.tableRow}>
                      <td style={styles.tableCell}>{order.clientName}<br/><span style={{ fontSize: '11px', color: '#64748b' }}>{order.clientPhone}</span></td>
                      <td style={styles.tableCell}>{order.orderNumber || order.id.slice(-8)}</td>
                      <td style={styles.tableCell}>{order.items.map(i => `${i.name} x${i.quantity}`).join(', ')}</td>
                      <td style={styles.tableCell}>{order.totalAmount.toLocaleString()}</td>
                      <td style={{...styles.tableCell, color: '#d97706', fontWeight: '600'}}>{order.balanceDue.toLocaleString()} UGX</td>
                    </tr>
                  ))}
                  {paginatedCompletedOrders.length === 0 && <tr><td colSpan="5" style={styles.emptyState}>No completed projects with balance</td></tr>}
                </tbody>
                {totalCompletedPages > 1 && (
                  <tfoot><tr><td colSpan="5" style={styles.paginationCell}>
                    <div style={styles.groupPagination}>
                      <button className="pageBtn" onClick={() => setCompletedPage(p => Math.max(1, p-1))} disabled={completedPage === 1}>Previous</button>
                      <span style={{ margin: '0 16px' }}>Page {completedPage} of {totalCompletedPages}</span>
                      <button className="pageBtn" onClick={() => setCompletedPage(p => Math.min(totalCompletedPages, p+1))} disabled={completedPage === totalCompletedPages}>Next</button>
                    </div>
                  </td></tr></tfoot>
                )}
                {totalCompletedBalance > 0 && (
                  <tfoot><tr style={styles.groupFooter}>
                    <td colSpan="3" style={{ textAlign: 'right', fontWeight: 'bold' }}>Subtotal (all):</td>
                    <td colSpan="2" style={{ fontWeight: 'bold', color: '#d97706' }}>{totalCompletedBalance.toLocaleString()} UGX</td>
                  </tr></tfoot>
                )}
              </table>
            </div>
          </div>

          {/* Ongoing Projects */}
          <div style={styles.groupSection}>
            <div style={styles.groupHeader}>
              <span style={styles.groupTitle}>⏳ Ongoing / Pending Projects</span>
              <span style={styles.groupTotals}>Outstanding: {totalOngoingBalance.toLocaleString()} UGX</span>
            </div>
            <div style={styles.tableWrapper}>
              <table style={styles.table}>
                <thead><tr style={styles.tableHeaderRow}>
                  <th>Client</th><th>Order #</th><th>Products</th><th>Total (UGX)</th><th>Balance Due</th>
                </tr></thead>
                <tbody>
                  {paginatedOngoingOrders.map((order, idx) => (
                    <tr key={idx} style={styles.tableRow}>
                      <td style={styles.tableCell}>{order.clientName}<br/><span style={{ fontSize: '11px', color: '#64748b' }}>{order.clientPhone}</span></td>
                      <td style={styles.tableCell}>{order.orderNumber || order.id.slice(-8)}</td>
                      <td style={styles.tableCell}>{order.items.map(i => `${i.name} x${i.quantity}`).join(', ')}</td>
                      <td style={styles.tableCell}>{order.totalAmount.toLocaleString()}</td>
                      <td style={{...styles.tableCell, color: '#d97706', fontWeight: '600'}}>{order.balanceDue.toLocaleString()} UGX</td>
                    </tr>
                  ))}
                  {paginatedOngoingOrders.length === 0 && <tr><td colSpan="5" style={styles.emptyState}>No ongoing projects with balance</td></tr>}
                </tbody>
                {totalOngoingPages > 1 && (
                  <tfoot><tr><td colSpan="5" style={styles.paginationCell}>
                    <div style={styles.groupPagination}>
                      <button className="pageBtn" onClick={() => setOngoingPage(p => Math.max(1, p-1))} disabled={ongoingPage === 1}>Previous</button>
                      <span style={{ margin: '0 16px' }}>Page {ongoingPage} of {totalOngoingPages}</span>
                      <button className="pageBtn" onClick={() => setOngoingPage(p => Math.min(totalOngoingPages, p+1))} disabled={ongoingPage === totalOngoingPages}>Next</button>
                    </div>
                  </td></tr></tfoot>
                )}
                {totalOngoingBalance > 0 && (
                  <tfoot><tr style={styles.groupFooter}>
                    <td colSpan="3" style={{ textAlign: 'right', fontWeight: 'bold' }}>Subtotal (all):</td>
                    <td colSpan="2" style={{ fontWeight: 'bold', color: '#d97706' }}>{totalOngoingBalance.toLocaleString()} UGX</td>
                  </tr></tfoot>
                )}
              </table>
            </div>
          </div>
        </div>

        {/* RIGHT: Accounts Payable */}
        <div style={styles.column}>
          <div style={styles.columnHeader}>
            <h2 style={styles.columnTitle}>💰 Accounts Payable</h2>
            <div style={styles.summaryCard}>
              <div><span style={styles.summaryLabel}>Total Payables</span><span style={styles.summaryValue}>{totalPayablesBalance.toLocaleString()} UGX</span></div>
              <div><span style={styles.summaryLabel}>Total Suggestions</span><span style={styles.summaryValue}>{totalSuggestions.toLocaleString()} UGX</span></div>
              <div><span style={styles.summaryLabel}>Planned Payments</span><span style={styles.summaryValue}>{totalPlannedPayments.toLocaleString()} UGX</span></div>
            </div>
          </div>

          {Object.entries(groupedPayables).map(([key, group]) => {
            const { paginated, page, totalPages } = getPaginatedGroupItems(key, group.items);
            return (
              <div key={key} style={styles.groupSection}>
                <div style={styles.groupHeader}>
                  <span style={styles.groupTitle}>{group.label}</span>
                  <div style={styles.groupTotals}>
                    <span>Balance: {group.totalBalance.toLocaleString()} UGX</span>
                    <span style={{ marginLeft: '20px', color: '#8b5cf6' }}>Suggested: {group.totalSuggested.toLocaleString()} UGX</span>
                    <span style={{ marginLeft: '20px', color: '#10b981' }}>Planned: {group.totalPlanned.toLocaleString()} UGX</span>
                  </div>
                </div>
                <div style={styles.tableWrapper}>
                  <table style={styles.tablePremium}>
                    <thead>
                      <tr style={styles.tableHeaderRowPremium}>
                        <th style={styles.thPremium}>Name / Description</th>
                        <th style={styles.thPremium}>Balance Due</th>
                        <th style={styles.thPremium}>Suggestion</th>
                        <th style={styles.thPremium}>To Be Paid</th>
                        <th style={styles.thPremium}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginated.map(payable => {
                        const statusBadge = payable.status === 'paid' ? { background: '#d1fae5', color: '#059669', text: '✓ Paid' } :
                                          payable.status === 'partial' ? { background: '#fef3c7', color: '#d97706', text: '⏳ Partial' } :
                                          { background: '#fee2e2', color: '#dc2626', text: '⚠️ Pending' };
                        const suggestionRaw = getPlanning(payable.id, 'suggestion');
                        const toBePaidRaw = getPlanning(payable.id, 'toBePaid');
                        return (
                          <tr key={payable.id} style={styles.tableRowPremium}>
                            <td style={styles.tdPremium}>
                              <div style={{ fontWeight: '600', marginBottom: '4px' }}>{payable.name}</div>
                              <div style={{ fontSize: '11px', color: '#64748b' }}>{payable.description || '—'}</div>
                            </td>
                            <td style={{...styles.tdPremium, color: '#f59e0b', fontWeight: '600', whiteSpace: 'nowrap'}}>
                              {payable.currentBalance.toLocaleString()} UGX
                            </td>
                            <td style={styles.tdPremium}>
                              <div style={styles.inputWrapper}>
                                <input
                                  type="text"
                                  style={styles.planInputPremium}
                                  placeholder="Suggestion"
                                  value={suggestionRaw === 0 ? '' : formatMoney(suggestionRaw)}
                                  onChange={(e) => {
                                    const raw = e.target.value;
                                    let cleaned = raw.replace(/[^0-9.]/g, '');
                                    const parts = cleaned.split('.');
                                    if (parts.length > 2) cleaned = parts[0] + '.' + parts.slice(1).join('');
                                    const numValue = cleaned === '' ? '' : parseFloat(cleaned);
                                    updatePlanning(payable.id, 'suggestion', numValue === '' ? 0 : numValue);
                                  }}
                                />
                                {saving[`${payable.id}_suggestion`] && <span style={styles.savingIndicator}>💾</span>}
                              </div>
                            </td>
                            <td style={styles.tdPremium}>
                              <div style={styles.inputWrapper}>
                                <input
                                  type="text"
                                  style={{...styles.planInputPremium, background: '#fef3c7', borderColor: '#f59e0b'}}
                                  placeholder="To be paid"
                                  value={toBePaidRaw === 0 ? '' : formatMoney(toBePaidRaw)}
                                  onChange={(e) => {
                                    const raw = e.target.value;
                                    let cleaned = raw.replace(/[^0-9.]/g, '');
                                    const parts = cleaned.split('.');
                                    if (parts.length > 2) cleaned = parts[0] + '.' + parts.slice(1).join('');
                                    const numValue = cleaned === '' ? '' : parseFloat(cleaned);
                                    updatePlanning(payable.id, 'toBePaid', numValue === '' ? 0 : numValue);
                                  }}
                                />
                                {saving[`${payable.id}_toBePaid`] && <span style={styles.savingIndicator}>💾</span>}
                              </div>
                            </td>
                            <td style={styles.tdPremium}>
                              <span style={{...styles.statusBadge, background: statusBadge.background, color: statusBadge.color, padding: '6px 12px', borderRadius: '20px', fontSize: '12px'}}>
                                {statusBadge.text}
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                      {paginated.length === 0 && <tr><td colSpan="5" style={styles.emptyStatePremium}>No items in this group</td></tr>}
                    </tbody>
                    {totalPages > 1 && (
                      <tfoot>
                        <tr><td colSpan="5" style={styles.paginationCell}>
                          <div style={styles.groupPagination}>
                            <button className="pageBtn" onClick={() => setGroupPage(key, Math.max(1, page-1))} disabled={page === 1}>Previous</button>
                            <span style={{ margin: '0 16px' }}>Page {page} of {totalPages}</span>
                            <button className="pageBtn" onClick={() => setGroupPage(key, Math.min(totalPages, page+1))} disabled={page === totalPages}>Next</button>
                          </div>
                        </td></tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              </div>
            );
          })}
          {payables.length === 0 && <div style={styles.emptyStatePremium}>No payables found</div>}
        </div>
      </div>
    </div>
  );
};

// Styles – no pseudo‑selectors inside objects; they are moved to the global style sheet.
const styles = {
  container: { padding: '28px', background: '#f8fafc', minHeight: '100vh' },
  loadingContainer: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '60px', color: '#64748b' },
  spinner: { width: '40px', height: '40px', border: '3px solid #e2e8f0', borderTopColor: '#7f1a2e', borderRadius: '50%', animation: 'spin 1s linear infinite', marginBottom: '16px' },
  twoColumns: { display: 'flex', gap: '28px', flexWrap: 'wrap' },
  column: { flex: 1, minWidth: '520px' },
  columnHeader: { marginBottom: '24px' },
  columnTitle: { fontSize: '22px', fontWeight: '700', color: '#0f172a', marginBottom: '16px' },
  summaryCard: { display: 'flex', gap: '32px', background: '#ffffff', padding: '20px 24px', borderRadius: '20px', border: '1px solid #eef2f6', marginBottom: '20px', boxShadow: '0 1px 2px rgba(0,0,0,0.03)' },
  summaryLabel: { fontSize: '12px', fontWeight: '500', textTransform: 'uppercase', letterSpacing: '0.5px', color: '#64748b', display: 'block', marginBottom: '6px' },
  summaryValue: { fontSize: '22px', fontWeight: '700', color: '#0f172a', display: 'block' },
  searchBar: { marginBottom: '20px' },
  searchInput: { width: '100%', padding: '12px 16px', border: '1px solid #e2e8f0', borderRadius: '12px', fontSize: '14px', outline: 'none' },
  groupSection: { marginBottom: '32px' },
  groupHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', background: '#f1f5f9', borderRadius: '16px', marginBottom: '16px' },
  groupTitle: { fontWeight: '700', fontSize: '16px', color: '#1e293b' },
  groupTotals: { fontSize: '13px', fontWeight: '500', color: '#475569' },
  tableWrapper: { overflowX: 'auto', borderRadius: '16px', border: '1px solid #eef2f6', background: '#ffffff' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  tableHeaderRow: { background: '#f8fafc', borderBottom: '1px solid #eef2f6' },
  tableRow: { borderBottom: '1px solid #f1f5f9' },
  tableCell: { padding: '14px 16px', color: '#334155' },
  groupFooter: { background: '#f8fafc', fontWeight: 'bold', borderTop: '1px solid #eef2f6' },
  emptyState: { textAlign: 'center', padding: '48px', color: '#94a3b8', fontSize: '13px' },
  paginationCell: { padding: '16px 20px', background: '#fafcff', borderTop: '1px solid #eef2f6', textAlign: 'center' },
  groupPagination: { display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px' },
  tablePremium: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  tableHeaderRowPremium: { background: '#f8fafc', borderBottom: '1px solid #eef2f6' },
  thPremium: { textAlign: 'left', padding: '16px 20px', fontWeight: '600', color: '#475569', fontSize: '13px' },
  tableRowPremium: { borderBottom: '1px solid #f1f5f9' },
  tdPremium: { padding: '16px 20px', color: '#334155', verticalAlign: 'top' },
  inputWrapper: { display: 'flex', alignItems: 'center', gap: '8px' },
  planInputPremium: { width: '130px', padding: '8px 12px', border: '1px solid #e2e8f0', borderRadius: '10px', fontSize: '13px', textAlign: 'right', outline: 'none' },
  savingIndicator: { marginLeft: '4px', fontSize: '12px', opacity: 0.7 },
  statusBadge: { display: 'inline-block', padding: '6px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: '500', whiteSpace: 'nowrap' },
  emptyStatePremium: { textAlign: 'center', padding: '48px', color: '#94a3b8', fontSize: '13px' }
};

// Global styles for hover/disabled effects
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes spin { to { transform: rotate(360deg); } }
  .pageBtn {
    padding: 6px 14px;
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    font-size: 12px;
    cursor: pointer;
    transition: all 0.2s;
  }
  .pageBtn:hover:not(:disabled) {
    background: #f1f5f9;
  }
  .pageBtn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  input:focus {
    border-color: #7f1a2e !important;
    box-shadow: 0 0 0 2px rgba(127,26,46,0.1);
  }
  tr:hover {
    background: #fafcff;
  }
`;
document.head.appendChild(styleSheet);

export default ReceivablesVsPayables;